import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Monitor, Smartphone,
  Globe, ShieldOff, RefreshCw, CheckCircle2
} from 'lucide-react';
import userService from '../../services/user.service';
import { useAuth } from '../../hooks/useAuth';
import { User, UserSession, RoleCode } from '../../types';

const EditUserPage = () => {
  const { id }      = useParams<{ id: string }>();
  const navigate    = useNavigate();
  const { user: currentUser, hasRole } = useAuth();
  const isAdmin     = hasRole('ADMIN');

  const [user,     setUser]     = useState<User | null>(null);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const [form, setForm] = useState({
    full_name:  '',
    email:      '',
    mobile:     '',
    role_code:  '' as RoleCode,
    is_active:  true,
  });

  // ── Load user data ──────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const [userData, sessionData] = await Promise.all([
          userService.getUserById(parseInt(id)),
          isAdmin ? userService.getUserSessions(parseInt(id)) : Promise.resolve([]),
        ]);
        setUser(userData);
        setSessions(sessionData);
        setForm({
          full_name: userData.full_name,
          email:     userData.email     || '',
          mobile:    userData.mobile    || '',
          role_code: userData.role_code,
          is_active: userData.is_active,
        });
      } catch (err: any) {
        setError('Failed to load user details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isAdmin]);

  // ── Save changes ────────────────────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError('');
    try {
      await userService.updateUser(parseInt(id), {
        full_name: form.full_name,
        email:     form.email     || undefined,
        mobile:    form.mobile    || undefined,
        role_code: form.role_code,
        is_active: form.is_active,
      });
      setSuccess('User updated successfully.');
      setTimeout(() => {
        setSuccess('');
        navigate('/users');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user.');
    } finally {
      setSaving(false);
    }
  };

  // ── Revoke all sessions ─────────────────────────────────────
  const handleRevokeAll = async () => {
    if (!id) return;
    try {
      await userService.revokeAllSessions(parseInt(id));
      setSessions([]);
      setSuccess('All sessions revoked. User will be logged out.');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to revoke sessions.');
    }
  };

  // ── Platform icon ───────────────────────────────────────────
  const PlatformIcon = ({ platform }: { platform: string }) => {
    if (platform === 'ANDROID' || platform === 'IOS')
      return <Smartphone size={14} className="text-gray-400" />;
    return <Monitor size={14} className="text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page-container">
        <div className="alert-error">User not found.</div>
      </div>
    );
  }

  return (
    <div className="page-container max-w-3xl">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/users')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={18} className="text-gray-500" />
        </button>
        <div>
          <h1 className="page-title">Edit User</h1>
          <p className="text-sm text-gray-500">@{user.username}</p>
        </div>
      </div>

      {error   && <div className="alert-error mb-4"><span>{error}</span></div>}
      {success && (
        <div className="alert-success mb-4">
          <CheckCircle2 size={16} />
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left — Edit form (2/3 width) */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-700">User Information</h2>
              <span className={`badge ${user.is_active ? 'badge-green' : 'badge-red'}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="card-body">
              <form onSubmit={handleSave} className="space-y-4">

                {/* Full name */}
                <div>
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.full_name}
                    onChange={e => setForm({ ...form, full_name: e.target.value })}
                    disabled={saving}
                  />
                </div>

                {/* Username — read only */}
                <div>
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-input bg-gray-50 text-gray-500"
                    value={user.username}
                    readOnly
                  />
                  <p className="text-xs text-gray-400 mt-1">Username cannot be changed</p>
                </div>

                {/* Email + Mobile */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="form-label">Mobile</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={form.mobile}
                      onChange={e => setForm({ ...form, mobile: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Role — admin only */}
                {isAdmin && (
                  <div>
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={form.role_code}
                      onChange={e => setForm({ ...form, role_code: e.target.value as RoleCode })}
                      disabled={saving || user.user_id === currentUser?.user_id}
                    >
                      <optgroup label="RBD Staff">
                        {(['ADMIN','MD','PM','SITE_ENG','ACCOUNTS','PURCHASE','HR','ESTIMATOR'] as RoleCode[])
                          .map(r => <option key={r} value={r}>{r}</option>)}
                      </optgroup>
                      <optgroup label="Portal Users">
                        {(['CUSTOMER','SUPPLIER','VENDOR','SUBCONTRACTOR'] as RoleCode[])
                          .map(r => <option key={r} value={r}>{r}</option>)}
                      </optgroup>
                    </select>
                    {user.user_id === currentUser?.user_id && (
                      <p className="text-xs text-gray-400 mt-1">
                        You cannot change your own role
                      </p>
                    )}
                  </div>
                )}

                {/* Active status — admin only, not self */}
                {isAdmin && user.user_id !== currentUser?.user_id && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Account Active</p>
                      <p className="text-xs text-gray-400">
                        Inactive users cannot login
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, is_active: !form.is_active })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full
                                  transition-colors ${form.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white
                                       shadow transition-transform ${
                                         form.is_active ? 'translate-x-6' : 'translate-x-1'
                                       }`} />
                    </button>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/users')}
                    className="btn-secondary flex-1 justify-center"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1 justify-center"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right — User info + sessions (1/3 width) */}
        <div className="space-y-4">

          {/* User meta info */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-gray-700 text-sm">Account Info</h3>
            </div>
            <div className="card-body space-y-3">
              {[
                { label: 'User Type',    value: user.user_type },
                { label: 'Tenant',       value: user.tenant_id },
                { label: 'Platform',     value: user.platform },
                { label: 'Failed Attempts', value: String(user.failed_attempts) },
                { label: 'Created',
                  value: new Date(user.created_at).toLocaleDateString('en-IN') },
                { label: 'Last Login',
                  value: user.last_login_at
                    ? new Date(user.last_login_at).toLocaleDateString('en-IN')
                    : 'Never' },
              ].map(item => (
                <div key={item.label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{item.label}</span>
                  <span className="font-medium text-gray-700">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Active sessions */}
          {isAdmin && (
            <div className="card">
              <div className="card-header">
                <h3 className="font-semibold text-gray-700 text-sm">
                  Active Sessions ({sessions.length})
                </h3>
                {sessions.length > 0 && (
                  <button
                    onClick={handleRevokeAll}
                    className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                  >
                    <ShieldOff size={12} />
                    Revoke All
                  </button>
                )}
              </div>
              <div className="card-body">
                {sessions.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-2">
                    No active sessions
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sessions.map(session => (
                      <div key={session.session_id}
                        className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                        <PlatformIcon platform={session.platform} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-700">
                            {session.platform}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {session.ip_address}
                          </p>
                          <p className="text-xs text-gray-400">
                            Active: {new Date(session.last_active_at)
                              .toLocaleTimeString('en-IN', {
                                hour: '2-digit', minute: '2-digit'
                              })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditUserPage;